import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Plus, Loader2, Baby, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useCheckin } from "../context/CheckinContext";
import { addChildToParent, editChild, createCheckin, recordConsent, apiError } from "../lib/api";
import { calcAgeMonths, formatAgeHost } from "../lib/utils";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ConsentSections } from "../components/ConsentSections";
import { LEGAL_VERSIONS } from "../legal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";

const dateRe = /^\d{4}-\d{2}-\d{2}$/;

export default function SelectChildren() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { flow, update } = useCheckin();
  const [selected, setSelected] = useState(flow.selected || []);
  const [children, setChildren] = useState(flow.children || []);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newKid, setNewKid] = useState({ name: "", birthday: "" });
  const [adding, setAdding] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editKid, setEditKid] = useState({ id: "", name: "", birthday: "" });
  const [editing, setEditing] = useState(false);
  const [youngWarnOpen, setYoungWarnOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentCommercial, setConsentCommercial] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentRecorded, setConsentRecorded] = useState(false);
  const [presentedAt, setPresentedAt] = useState(null);

  useEffect(() => {
    if (!flow.parent?.id) navigate("/parent/phone");
  }, [flow.parent, navigate]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const allSelected = children.length > 0 && selected.length === children.length;
  const toggleAll = () =>
    setSelected(allSelected ? [] : children.map((c) => c.id));

  const handleAddChild = async () => {
    if (!newKid.name.trim()) return toast.error(t("childNameRequired"));
    if (!dateRe.test(newKid.birthday.trim())) return toast.error(t("useDateFormat"));
    setAdding(true);
    try {
      const res = await addChildToParent(flow.parent.id, {
        name: newKid.name.trim(),
        birthday: newKid.birthday.trim(),
      });
      const updated = [...children, { id: res.id, name: res.name, birthday: res.birthday }];
      setChildren(updated);
      setSelected((s) => [...s, res.id]);
      setNewKid({ name: "", birthday: "" });
      setAddOpen(false);
    } catch (e) {
      toast.error(apiError(e) || t("failedToAddChild"));
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (child) => {
    setEditKid({ id: child.id, name: child.name, birthday: child.birthday || "" });
    setEditOpen(true);
  };

  const handleEditChild = async () => {
    if (!editKid.name.trim()) return toast.error(t("childNameRequired"));
    if (!dateRe.test(editKid.birthday.trim())) return toast.error(t("useDateFormat"));
    setEditing(true);
    try {
      const res = await editChild(flow.parent.id, editKid.id, {
        name: editKid.name.trim(),
        birthday: editKid.birthday.trim(),
        phone_number: flow.parent.phone_number,
      });
      setChildren((list) =>
        list.map((c) => (c.id === editKid.id ? { ...c, name: res.name, birthday: res.birthday } : c))
      );
      setEditOpen(false);
      toast.success(t("childUpdated"));
    } catch (e) {
      toast.error(apiError(e) || t("failedToUpdateChild"));
    } finally {
      setEditing(false);
    }
  };

  const doCheckin = async () => {
    setLoading(true);
    try {
      const res = await createCheckin(flow.parent.id, selected);
      const checkedIn = children.filter((c) => selected.includes(c.id));
      update({ children, selected, result: { ...res, checkedIn } });
      navigate("/parent/confirmation");
    } catch (e) {
      toast.error(apiError(e) || t("failedToCheckin"));
    } finally {
      setLoading(false);
    }
  };

  const proceedAfterConsent = () => {
    const hasYoung = children.some((c) => {
      if (!selected.includes(c.id)) return false;
      const m = calcAgeMonths(c.birthday);
      return m != null && m <= 24;
    });
    if (hasYoung) {
      setYoungWarnOpen(true);
    } else {
      doCheckin();
    }
  };

  const handleCheckin = () => {
    if (selected.length === 0) return toast.error(t("pleaseSelectChild"));
    if (flow.needsConsent && !consentRecorded) {
      setPresentedAt(new Date().toISOString());
      setConsentOpen(true);
      return;
    }
    proceedAfterConsent();
  };

  const saveConsentAndContinue = async () => {
    setConsentSaving(true);
    try {
      await recordConsent(flow.parent.id, {
        marketing_data_consent: consentMarketing,
        commercial_electronic_message_consent: consentCommercial,
        marketing_data_version: LEGAL_VERSIONS.marketing_data,
        commercial_message_version: LEGAL_VERSIONS.commercial_electronic_message,
        presented_at: presentedAt,
        child_ids: selected,
      });
      setConsentRecorded(true);
      setConsentOpen(false);
      proceedAfterConsent();
    } catch (e) {
      toast.error(apiError(e) || t("somethingWentWrong"));
    } finally {
      setConsentSaving(false);
    }
  };

  return (
    <div className="min-h-screen lf-grain bg-[#FFEEBC]" data-testid="select-children-page">
      <header className="mx-auto flex max-w-2xl items-center justify-between px-5 py-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/parent/phone")}
          className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#26422A] hover:bg-[#26422A]/10"
        >
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>
        <LanguageSwitcher />
      </header>

      <main className="mx-auto max-w-2xl px-5 pb-32">
        <div className="mb-2 flex items-end justify-between">
          <div>
            <h1 className="font-display text-2xl font-black text-[#26422A]">
              {t("selectChildren")}
            </h1>
            <p className="text-sm font-medium text-[#26422A]">
              {flow.parent?.name} {flow.parent?.surname}
            </p>
          </div>
          {children.length > 0 && (
            <button
              data-testid="select-all-btn"
              onClick={toggleAll}
              className="text-sm font-bold text-[#26422A] hover:underline"
            >
              {t("selectAll")}
            </button>
          )}
        </div>

        <div className="mt-4 space-y-3">
          {children.map((c, i) => {
            const isSel = selected.includes(c.id);
            const ageLabel = formatAgeHost(c.birthday, t);
            return (
              <motion.div
                key={c.id}
                data-testid={`child-select-${i}`}
                role="button"
                tabIndex={0}
                onClick={() => toggle(c.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(c.id); } }}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`flex w-full cursor-pointer items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors ${
                  isSel
                    ? "border-[#26422A] bg-[#26422A] text-[#FFEEBC]"
                    : "border-[#26422A]/40 bg-[#FFFFFF] text-[#26422A]"
                }`}
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                    isSel ? "bg-[#FFEEBC]" : "bg-[#FFEEBC]"
                  }`}
                >
                  <Baby className={`h-6 w-6 ${isSel ? "text-[#26422A]" : "text-[#26422A]"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-lg font-bold">{c.name}</div>
                  {ageLabel && (
                    <div className={`text-sm ${isSel ? "text-[#FFEEBC]/70" : "text-[#26422A]"}`}>
                      {ageLabel}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`edit-child-${i}`}
                  onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                    isSel
                      ? "border-[#FFEEBC]/50 text-[#FFEEBC] hover:bg-[#FFEEBC]/10"
                      : "border-[#26422A]/40 text-[#26422A] hover:bg-[#26422A]/10"
                  }`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t("editInfo")}</span>
                </button>
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                    isSel ? "border-[#FFEEBC] bg-[#FFEEBC]" : "border-[#26422A]"
                  }`}
                >
                  {isSel && <Check className="h-4 w-4 text-[#26422A]" strokeWidth={3} />}
                </div>
              </motion.div>
            );
          })}

          <Button
            data-testid="open-add-child-btn"
            variant="outline"
            onClick={() => setAddOpen(true)}
            className="h-14 w-full rounded-2xl border-2 border-dashed border-[#26422A] bg-transparent font-bold text-[#26422A] hover:bg-[#26422A]/10"
          >
            <Plus className="mr-2 h-5 w-5" /> {t("addNewChild")}
          </Button>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 max-w-full overflow-x-hidden border-t border-[#26422A]/30 bg-[#FFFFFF]/95 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] backdrop-blur">
        <div className="mx-auto w-full max-w-2xl">
          <div className="flex items-center gap-4">
            <div className="min-w-0 shrink text-sm font-bold text-[#26422A]" data-testid="selected-count">
              {selected.length} {t("childrenSelected")}
            </div>
            <Button
              data-testid="submit-checkin-btn"
              onClick={handleCheckin}
              disabled={loading || selected.length === 0}
              className="ml-auto h-14 min-w-0 flex-1 rounded-xl bg-[#EA672D] text-base font-bold text-[#FFFFFF] hover:bg-[#d4571f] sm:flex-none sm:px-10"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("submitCheckin")}
            </Button>
          </div>
          <p className="mt-2 text-center text-sm font-semibold leading-snug text-[#B42318]" data-testid="payment-reminder">
            {t("paymentReminder")}
          </p>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="add-child-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#26422A]">
              {t("addNewChild")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("childName")}</Label>
              <Input
                data-testid="new-child-name"
                value={newKid.name}
                onChange={(e) => setNewKid((k) => ({ ...k, name: e.target.value }))}
                placeholder={t("enterChildName")}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("birthday")}</Label>
              <Input
                data-testid="new-child-birthday"
                type="date"
                value={newKid.birthday}
                onChange={(e) => setNewKid((k) => ({ ...k, birthday: e.target.value }))}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              className="rounded-xl border-[#26422A] font-bold text-[#26422A]"
            >
              {t("cancel")}
            </Button>
            <Button
              data-testid="confirm-add-child-btn"
              onClick={handleAddChild}
              disabled={adding}
              className="rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : t("addChild")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(v) => { if (!editing) setEditOpen(v); }}>
        <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="edit-child-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#26422A]">
              {t("editChildTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("childName")}</Label>
              <Input
                data-testid="edit-child-name"
                value={editKid.name}
                onChange={(e) => setEditKid((k) => ({ ...k, name: e.target.value }))}
                placeholder={t("enterChildName")}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("birthday")}</Label>
              <Input
                data-testid="edit-child-birthday"
                type="date"
                value={editKid.birthday}
                onChange={(e) => setEditKid((k) => ({ ...k, birthday: e.target.value }))}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              className="rounded-xl border-[#26422A] font-bold text-[#26422A]"
            >
              {t("cancel")}
            </Button>
            <Button
              data-testid="save-child-btn"
              onClick={handleEditChild}
              disabled={editing}
              className="rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {editing ? <Loader2 className="h-5 w-5 animate-spin" /> : t("saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={consentOpen} onOpenChange={(v) => { if (!consentSaving) setConsentOpen(v); }}>
        <DialogContent
          data-testid="consent-catchup-dialog"
          className="max-h-[88vh] overflow-y-auto rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#26422A]">
              {t("consentTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#26422A]/80">{t("consentOptionalHint")}</p>
          <div className="py-2">
            <ConsentSections
              marketingChecked={consentMarketing}
              setMarketingChecked={setConsentMarketing}
              commercialChecked={consentCommercial}
              setCommercialChecked={setConsentCommercial}
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="consent-continue-btn"
              onClick={saveConsentAndContinue}
              disabled={consentSaving}
              className="h-12 w-full rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {consentSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("continue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={youngWarnOpen} onOpenChange={setYoungWarnOpen}>
        <AlertDialogContent className="rounded-3xl border-2 border-[#EA672D]/50 bg-white" data-testid="young-kid-warning-dialog">
          <AlertDialogHeader>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EA672D]/15">
              <AlertTriangle className="h-6 w-6 text-[#EA672D]" />
            </div>
            <AlertDialogTitle className="font-display text-xl text-[#26422A]">
              {t("youngKidWarningTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#26422A]">
              {t("youngKidWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl border-[#26422A]/40 font-bold text-[#26422A]">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="young-kid-continue-btn"
              onClick={() => {
                setYoungWarnOpen(false);
                doCheckin();
              }}
              className="rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {t("continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
